/**
 * Presentation mapping from the shared GHG calculation result to report view-model
 * fields. Does not compute emissions; it only copies values already produced by
 * fetchGHGInventoryData.
 */

import {
  LIVE_CHART_SECTOR_LABELS,
  extractHistoricalTotals,
  extractSectorValue,
  extractYearTotal,
  inventoryYearFromResult,
  type GHGInventoryResult,
} from '../../services/ghgInventoryCalculationService';
import type { HistoricalYearPoint, ReportSectorObservation } from './types';
import { reportValue } from './valueHelpers';

const LIVE_SOURCE = 'fetchGHGInventoryData';

const SECTOR_MAP: Array<{
  sectorCode: string;
  sectorLabel: string;
  chartLabel: string;
}> = [
  { sectorCode: 'ldv', sectorLabel: 'Light-duty vehicles', chartLabel: LIVE_CHART_SECTOR_LABELS.LDV },
  { sectorCode: 'hdv', sectorLabel: 'Heavy-duty vehicles', chartLabel: LIVE_CHART_SECTOR_LABELS.HDV },
  { sectorCode: 'elec_res', sectorLabel: 'Residential electricity', chartLabel: LIVE_CHART_SECTOR_LABELS.ELEC_RES },
  { sectorCode: 'elec_com', sectorLabel: 'Commercial electricity', chartLabel: LIVE_CHART_SECTOR_LABELS.ELEC_COM },
  { sectorCode: 'ng_res', sectorLabel: 'Residential natural gas', chartLabel: LIVE_CHART_SECTOR_LABELS.NG_RES },
  { sectorCode: 'ng_com', sectorLabel: 'Commercial natural gas', chartLabel: LIVE_CHART_SECTOR_LABELS.NG_COM },
  { sectorCode: 'waste', sectorLabel: 'Waste & wastewater', chartLabel: LIVE_CHART_SECTOR_LABELS.WASTE },
  { sectorCode: 'offroad', sectorLabel: 'Off-road', chartLabel: LIVE_CHART_SECTOR_LABELS.OFFROAD },
  { sectorCode: 'agriculture', sectorLabel: 'Agriculture', chartLabel: LIVE_CHART_SECTOR_LABELS.AGRICULTURE },
];

export function mapLiveResultToSectorObservations(
  result: GHGInventoryResult,
  year: number
): ReportSectorObservation[] {
  const sectors: ReportSectorObservation[] = SECTOR_MAP.map(({ sectorCode, sectorLabel, chartLabel }) => {
    const value = extractSectorValue(result, chartLabel, year);
    return {
      sectorCode,
      sectorLabel,
      emissionsMt: reportValue(value, 'MT CO2e', value == null ? 'missing' : 'modeled', {
        sourceTable: LIVE_SOURCE,
        sourceColumn: chartLabel,
        calculationRule: 'Copied from the shared GHG page calculation result (no independent formula)',
        sourceYear: year,
      }),
      sourceStatusText: 'climateplans_live_ghg_inventory',
      sourceYear: year,
    };
  });

  sectors.push({
    sectorCode: 'water_wastewater',
    sectorLabel: 'Wastewater',
    emissionsMt: reportValue(null, 'MT CO2e', 'unresolved_breakout', {
      sourceTable: LIVE_SOURCE,
      calculationRule:
        'Not returned as a separate stacked series. On the GHG inventory page, wastewater is combined with landfill waste as Waste & wastewater when a scaled estimate exists.',
      sourceYear: year,
    }),
    sourceStatusText: 'unresolved_breakout',
    sourceYear: year,
  });

  return sectors;
}

export function mapLiveResultToHistoricalPoints(
  result: GHGInventoryResult
): HistoricalYearPoint[] {
  return extractHistoricalTotals(result).map((p) => ({
    year: p.year,
    totalEmissionsMt: reportValue(p.totalMt, 'MT CO2e', 'modeled', {
      sourceTable: LIVE_SOURCE,
      sourceColumn: 'chartData yearly stack total',
      sourceYear: p.year,
      calculationRule: 'Sum of already-calculated sector values from fetchGHGInventoryData',
    }),
    ldvSource: 'climateplans_live_ghg_inventory',
  }));
}

export function mapLiveInventoryYear(result: GHGInventoryResult, preferredYear = 2023): number {
  return inventoryYearFromResult(result, preferredYear);
}

export function mapLiveInventoryTotal(result: GHGInventoryResult, year: number): number | null {
  const fromHeadline = year === 2023 ? result.totalEmissions2023 : undefined;
  if (fromHeadline != null && Number.isFinite(fromHeadline)) return fromHeadline;
  if (result.latestYear === year && result.totalEmissions != null && Number.isFinite(result.totalEmissions)) {
    return result.totalEmissions;
  }
  return extractYearTotal(result, year);
}
